import { tWithKeyAttr as t } from "../i18nHelper";
import { SeatSubscriptionDto } from "../services/api/featureCreditPackagesApi";
import { formatDateInIsrael } from "./utils";

// Everything that turns a seat-pack row into state and copy lives here, because SeatPacksTab is not
// the only consumer: the paid→free downgrade dialog reads the same endpoint for its per-pack
// breakdown (GET ResourceCaps/Impact returns only the earliest EndsAt). Deriving any of this inside
// the tab would mean that dialog forks the rules.
//
// Shared: the state, the labels and the null-date guards. NOT shared: sortSeatPacks, which is
// presentation logic for this screen only — see the note at the bottom.

export type SeatPackState =
  | { kind: "renewing"; nextChargeOn: string } // Active, cancelAtPeriodEnd false
  | { kind: "ending"; endsOn: string | null } // cancelAtPeriodEnd true — null ⇒ imminently
  | { kind: "past_due"; graceEndsOn: string | null };

// graceEndsAt and effectiveAt are both nullable, and either can arrive unparseable. Both cases mean
// the same thing — we cannot name a day — which the copy renders as "imminently", never as
// "Invalid Date". Guarding once here keeps the check out of every call site.
const usableDate = (value: string | null | undefined): string | null =>
  value && !Number.isNaN(new Date(value).getTime()) ? value : null;

export const seatPackState = (row: SeatSubscriptionDto): SeatPackState => {
  // PastDue outranks cancelAtPeriodEnd: the money story is the urgent one, and a past-due row's
  // currentPeriodEnd is already in the past, so labelling it "next charge" would be wrong too.
  if (row.status === "PastDue") {
    return { kind: "past_due", graceEndsOn: usableDate(row.graceEndsAt) };
  }
  if (row.cancelAtPeriodEnd) {
    return { kind: "ending", endsOn: usableDate(row.currentPeriodEnd) };
  }
  return { kind: "renewing", nextChargeOn: row.currentPeriodEnd };
};

// The whole status cell, in one place. currentPeriodEnd is deliberately never rendered on a
// past-due row — it is stale by definition there.
export const seatPackStatusLabel = (row: SeatSubscriptionDto): string => {
  const state = seatPackState(row);
  switch (state.kind) {
    case "past_due":
      return state.graceEndsOn
        ? t("SEAT_PACK_PAST_DUE_GRACE", {
            count: row.seats,
            date: formatDateInIsrael(state.graceEndsOn),
          })
        : t("SEAT_PACK_PAST_DUE_GRACE_IMMINENT", { count: row.seats });
    case "ending":
      return state.endsOn
        ? t("SEAT_PACK_ENDS_ON", { date: formatDateInIsrael(state.endsOn) })
        : t("SEAT_PACK_ENDS_IMMINENTLY");
    case "renewing":
      return t("SEAT_PACK_NEXT_CHARGE", { date: formatDateInIsrael(state.nextChargeOn) });
  }
};

// "49 ILS per cycle" / "49 per cycle" — the one place the nullable currency is split, same
// convention as packageSizeLabel in featureCreditPackages.ts.
//
// An orphaned CurrencyId resolves to null server-side and the pack is served anyway, so the choice
// here is between an unlabelled amount and a wrong one. A Hebrew-locale admin shown a bare ₪ on a
// pack actually priced in USD is the worse outcome, so the currency token is dropped entirely
// rather than defaulted. "per cycle", not "per month": cycle length is not in the contract.
export const priceLabel = (row: SeatSubscriptionDto): string => {
  const price = row.pricePerCycle.toLocaleString(undefined, { maximumFractionDigits: 2 });
  return row.currency
    ? t("SEAT_PACK_PRICE_PER_CYCLE", { price, currency: row.currency })
    : t("SEAT_PACK_PRICE_PER_CYCLE_NO_CURRENCY", { price });
};

// The date the cancel confirmation is allowed to promise. null when there is no paid time left to
// reassure anyone about — a past-due pack, or a period that has already elapsed — which routes the
// dialog to its "ends imminently" copy. Showing a stale date there is worse than showing none.
export const cancelPromiseDate = (row: SeatSubscriptionDto): string | null => {
  if (row.status === "PastDue") return null;
  const periodEnd = usableDate(row.currentPeriodEnd);
  return periodEnd && new Date(periodEnd).getTime() > Date.now() ? periodEnd : null;
};

// ─── Banner aggregates ───────────────────────────────────────────────────────

export const pastDueRows = (rows: SeatSubscriptionDto[]): SeatSubscriptionDto[] =>
  rows.filter((row) => row.status === "PastDue");

export const seatsAtRisk = (rows: SeatSubscriptionDto[]): number =>
  pastDueRows(rows).reduce((total, row) => total + row.seats, 0);

// The first deadline that bites, not the last — the banner promises a date the user must act by.
export const earliestGraceEndsAt = (rows: SeatSubscriptionDto[]): string | null =>
  pastDueRows(rows)
    .map((row) => usableDate(row.graceEndsAt))
    .filter((date): date is string => date !== null)
    .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())[0] ?? null;

// ─── Presentation order for the seat-packs screen ────────────────────────────

const STATE_ORDER: Record<SeatPackState["kind"], number> = {
  past_due: 0,
  ending: 1,
  renewing: 2,
};

// Each group is ordered by the date that actually governs it — the grace deadline for a past-due
// pack, the paid-through date for the rest — never one key across all three. 0 for a missing date so
// an "imminently" row leads its group, and so the subtraction below can't produce NaN.
const groupDateMs = (row: SeatSubscriptionDto): number => {
  const state = seatPackState(row);
  const date =
    state.kind === "past_due"
      ? state.graceEndsOn
      : state.kind === "ending"
        ? state.endsOn
        : usableDate(state.nextChargeOn);
  return date ? new Date(date).getTime() : 0;
};

// The API orders by CurrentPeriodEnd ascending, deliberately, because that is what the downgrade
// dialog needs. It is the wrong order here: a past-due row's currentPeriodEnd is frozen in the past
// while the row sits in its grace window, so a renewing pack expiring next week sorts ahead of a
// past-due pack that needs action today, burying the only row that wants attention.
//
// Tie-broken on name so the order is stable across refetches — a row that jumps position between
// reads is its own small trust break on a billing screen.
export const sortSeatPacks = (rows: SeatSubscriptionDto[]): SeatSubscriptionDto[] =>
  [...rows].sort((a, b) => {
    const byState = STATE_ORDER[seatPackState(a).kind] - STATE_ORDER[seatPackState(b).kind];
    if (byState !== 0) return byState;

    const byDate = groupDateMs(a) - groupDateMs(b);
    if (byDate !== 0) return byDate;

    return a.name.localeCompare(b.name);
  });
